import { useEffect, useRef } from 'react';
import { useRecoilState } from 'recoil';

import TranslationConstant from '@/common/constant/TranslationConstant';
import { getListOnDemandAtomByPanelType } from '@/state/atom/translationAtom';
import {
    initListTicketOnDemand,
    autoTranslateNewListTickets
} from '@/component/multiLanguage/handlers/ListTicketOnDemandHandler';

/**
 * Drive the on-demand list-translation state for one ticket/knowledge list screen
 * (MLT-001-F4). Keeps the per-screen atom in sync with the rendered records and
 * auto-translates records that appear while the list toggle is ON (§4.3 / BR-3a).
 *
 * Shared by the 3 list screens (Q&A-customer / helpdesk / knowledge). The screen owns
 * its own reload modal, so the hook does not render it: pass setIsOpenModalReloadPage
 * and the hook opens it on a translation failure.
 *
 * @param {Object} params
 * @param {string} params.panelType - Constant.PANEL_TYPE.* (picks the per-screen atom).
 * @param {string} params.viewKey - current view key (enum for QA, viewId for helpdesk/knowledge).
 * @param {Array} params.items - the rendered list records (ticket / knowledge); only item.id is read.
 * @param {Function} [params.setIsOpenModalReloadPage] - opens the screen's reload modal on failure.
 */
const useListTicketOnDemand = ({ panelType, viewKey, items, setIsOpenModalReloadPage }) => {
    const listOnDemandAtom = getListOnDemandAtomByPanelType(panelType);
    const [listTicketOnDemand, setListTicketOnDemand] = useRecoilState(listOnDemandAtom);

    // Timer for debouncing (batching) auto-translation of new records.
    const autoTranslateTimerRef = useRef(null);
    // Track viewKey + list reference to skip the mismatched render (new viewKey, old list) on a view change.
    const viewKeyRef = useRef(null);
    const listRef = useRef(null);

    // Keep the on-demand state in sync with the rendered records. Runs on initial load and
    // on scroll / search / filter changes: new ids get a fresh record, ids already
    // translated in this session are kept (BR-6 / BR-8).
    useEffect(() => {
        const viewChanged = viewKeyRef.current !== viewKey;
        const listChanged = listRef.current !== items;
        viewKeyRef.current = viewKey;
        listRef.current = items;

        // Mismatched render: the view switched but the list is still the previous view's
        // (reference unchanged). Skip so the new view does not inherit the old view's ids;
        // the next run (list reloaded for the new view → reference changed) syncs correctly.
        if (viewChanged && !listChanged) {
            return;
        }

        initListTicketOnDemand(setListTicketOnDemand, panelType, viewKey, items);
    }, [items, viewKey]);

    // Auto-translate records that appear while the list toggle is ON (§4.3 / BR-3a).
    // Depend on this panel's record only (not the whole atom) so another list panel's change
    // does not re-run this effect. Debounced (kiểu CHT-005 §4.4): a burst of list changes
    // (scroll / search / filter — each ≤ 20 records) resets the timer so the pending records
    // are gathered and sent as ONE request.
    const listRecord = listTicketOnDemand[viewKey];
    useEffect(() => {
        if (autoTranslateTimerRef.current) {
            clearTimeout(autoTranslateTimerRef.current);
        }
        autoTranslateTimerRef.current = setTimeout(() => {
            autoTranslateNewListTickets({
                panelType,
                viewKey,
                listRecord,
                setListTicketOnDemand,
                setIsOpenModalReloadPage
            });
        }, TranslationConstant.AUTO_TRANSLATE_DEBOUNCE_MS);

        return () => {
            if (autoTranslateTimerRef.current) {
                clearTimeout(autoTranslateTimerRef.current);
            }
        };
    }, [listRecord, viewKey]);
};

export default useListTicketOnDemand;
